import { useState } from "react";
import SelectMenu from "./crm/SelectMenu";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30, 50];
const DEFAULT_ITEMS_PER_PAGE = 30;

function Pagination({
  currentPage,
  totalItems,
  onPageChange,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  onItemsPerPageChange,
}) {
  const [goToInput, setGoToInput] = useState("");
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalItems <= 20) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleGoToPage = (e) => {
    if (e.key === "Enter") {
      const page = parseInt(goToInput, 10);
      if (page >= 1 && page <= totalPages) {
        onPageChange(page);
      }
      setGoToInput("");
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 mt-2 border-t border-gray-700">
      {/* Left side: Display dropdown, Page info, Go to page */}
      <div className="flex items-center gap-4 text-sm text-gray-300">
        <div className="flex items-center gap-2">
          <span className="text-gray-400">Display:</span>
          <SelectMenu
            value={itemsPerPage}
            onChange={(e) => {
              if (onItemsPerPageChange) {
                onItemsPerPageChange(Number(e.target.value));
              }
            }}
            className="bg-[#1e1b4b] border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm outline-none cursor-pointer"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </SelectMenu>
        </div>

        <span className="text-gray-400">
          Page: <span className="text-gray-200 font-medium">{currentPage}</span>{" "}
          of <span className="text-gray-200 font-medium">{totalPages}</span>
        </span>

        <div className="flex items-center gap-2">
          <span className="text-gray-400">Go to page:</span>
          <input
            type="text"
            value={goToInput}
            onChange={(e) => setGoToInput(e.target.value)}
            onKeyDown={handleGoToPage}
            className="w-12 bg-[#1e1b4b] border border-gray-600 rounded px-2 py-1 text-gray-200 text-sm outline-none text-center"
          />
        </div>
      </div>

      {/* Right side: First, Prev, Page numbers, Next, Last */}
      <div className="flex items-center gap-1">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="First page"
        >
          <ChevronsLeft size={18} />
        </button>

        {/* Previous page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-1.5 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous page"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-0.5 mx-1">
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                currentPage === page
                  ? "text-yellow-400 border-b-2 border-yellow-400 font-bold"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Next page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next page"
        >
          <ChevronRight size={18} />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="p-1.5 rounded text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Last page"
        >
          <ChevronsRight size={18} />
        </button>
      </div>
    </div>
  );
}

export { DEFAULT_ITEMS_PER_PAGE as ITEMS_PER_PAGE };
export default Pagination;
